import { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useDeliveryLocation } from './DeliveryLocationContext';
import * as addressesApi from '../api/endpoints/addresses';
import { genericLocationLabel, locationFromSavedAddress } from '../utils/deliveryLocationLabels';

// A location still carries account data while it names a saved address.
const hasPrivateAddressDetails = ({ source, addressId, label, pincode }) =>
  Boolean(addressId) || (source === 'address' && (label || null) !== genericLocationLabel(pincode));

/**
 * Keeps the browsing location consistent with who is signed in. Renders
 * nothing. Kept apart from DeliveryLocationProvider so the location itself
 * stays independent of authentication.
 *
 * - Signed in with no browsing location: seed it from the default saved
 *   address as explicit geography (so headers are sent and the UI knows the
 *   place), marked NOT confirmed by the customer. An existing location —
 *   including one a guest chose before signing in — always wins.
 * - Signed out: keep the delivery area (pincode/coordinates) but drop the
 *   saved-address id and label that would show the previous account's address.
 */
const DeliveryLocationAccountSync = () => {
  const { authStatus } = useAuth();
  const location = useDeliveryLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  // Seeding is attempted once per signed-in session.
  const seededRef = useRef(false);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      seededRef.current = false;
      return undefined;
    }
    if (seededRef.current) return undefined;
    if (locationRef.current.locationKey) {
      seededRef.current = true;
      return undefined;
    }

    let active = true;
    addressesApi
      .listAddresses()
      .then((response) => {
        if (!active) return;
        seededRef.current = true;
        // The customer chose a location while the addresses were loading.
        if (locationRef.current.locationKey) return;
        const addresses = Array.isArray(response?.data) ? response.data : [];
        // Same default rule as the backend: the flagged address, else the first.
        const defaultAddress = addresses.find((address) => address?.isDefault) || addresses[0];
        const seeded = defaultAddress && locationFromSavedAddress(defaultAddress, { confirmedByUser: false });
        if (seeded) locationRef.current.setDeliveryLocation(seeded);
      })
      .catch(() => {
        // No seed; the customer can still choose a location themselves.
        if (active) seededRef.current = true;
      });
    return () => {
      active = false;
    };
  }, [authStatus]);

  const { locationKey, source, addressId, label, pincode, coordinates, confirmedByUser, setDeliveryLocation } = location;

  useEffect(() => {
    if (authStatus !== 'guest' || !locationKey) return;
    if (!hasPrivateAddressDetails({ source, addressId, label, pincode })) return;
    // Same geography, so this is a metadata-only update: no new
    // locationVersion and no new service-context request.
    setDeliveryLocation({
      pincode,
      coordinates,
      source,
      label: genericLocationLabel(pincode),
      addressId: null,
      confirmedByUser,
    });
  }, [authStatus, locationKey, source, addressId, label, pincode, coordinates, confirmedByUser, setDeliveryLocation]);

  return null;
};

export default DeliveryLocationAccountSync;
